import React, { useEffect, useRef, useState } from 'react';
import {
  getVideoGenerationStepsConfig,
  getCurrentVideoGenerationStep,
  getVideoGenerationStepProgress,
} from '../utils/videoGenerationSteps';
import './VideoGenerationSteps.css';

const BAR_TRANSITION_MS = 550;
const HOLD_AT_COMPLETE_MS = 480;
const CONTENT_EXIT_MS = 340;
const CONTENT_ENTER_MS = 340;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function usePerStepProgressAnimation(globalProgress, enabled, stepOptions) {
  const options = {
    includeAiSoundEffects: stepOptions?.includeAiSoundEffects === true,
    textOnScreen: stepOptions?.textOnScreen === true,
    noScript: stepOptions?.noScript === true,
  };
  const steps = getVideoGenerationStepsConfig(options).steps;
  const progressRef = useRef(globalProgress);
  progressRef.current = globalProgress;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const displayStepIndexRef = useRef(
    enabled ? getVideoGenerationStepProgress(globalProgress, options).stepIndex : 0,
  );
  const [displayStepIndex, setDisplayStepIndex] = useState(displayStepIndexRef.current);
  const [displayProgress, setDisplayProgress] = useState(() =>
    (enabled ? getVideoGenerationStepProgress(globalProgress, options).stepProgress : 0),
  );
  const [contentPhase, setContentPhase] = useState('visible');
  const [barInstant, setBarInstant] = useState(false);
  const advancingRef = useRef(false);
  const advanceGenRef = useRef(0);

  const syncStepIndex = (idx) => {
    displayStepIndexRef.current = idx;
    setDisplayStepIndex(idx);
  };

  const includeAiSoundEffects = options.includeAiSoundEffects;
  const textOnScreen = options.textOnScreen;
  const noScript = options.noScript;

  useEffect(() => {
    // Reset when step labels / SFX mode toggles so step indices stay in range
    const mapped = getVideoGenerationStepProgress(progressRef.current, optionsRef.current);
    syncStepIndex(mapped.stepIndex);
    setDisplayProgress(mapped.stepProgress);
    setContentPhase('visible');
    advancingRef.current = false;
  }, [includeAiSoundEffects, textOnScreen, noScript]);

  useEffect(() => {
    if (!enabled) return;

    const mapped = getVideoGenerationStepProgress(globalProgress, optionsRef.current);

    if (globalProgress === 0 && mapped.stepIndex === 0 && mapped.stepProgress === 0) {
      advancingRef.current = false;
      syncStepIndex(0);
      setDisplayProgress(0);
      setContentPhase('visible');
      return;
    }

    if (advancingRef.current) return;

    if (mapped.stepIndex === displayStepIndexRef.current) {
      setDisplayProgress((prev) => Math.max(prev, mapped.stepProgress));
      return;
    }

    if (mapped.stepIndex <= displayStepIndexRef.current) return;

    advancingRef.current = true;
    const advanceGen = ++advanceGenRef.current;
    let cancelled = false;

    (async () => {
      try {
        while (!cancelled && advanceGen === advanceGenRef.current) {
          const latest = getVideoGenerationStepProgress(progressRef.current, optionsRef.current);
          const currentIdx = displayStepIndexRef.current;
          const stepCount = getVideoGenerationStepsConfig(optionsRef.current).steps.length;

          if (latest.stepIndex <= currentIdx) {
            if (latest.stepIndex === currentIdx) {
              setDisplayProgress((prev) => Math.max(prev, latest.stepProgress));
            }
            break;
          }

          setDisplayProgress(100);
          await sleep(BAR_TRANSITION_MS);
          if (cancelled) break;
          await sleep(HOLD_AT_COMPLETE_MS);
          if (cancelled) break;

          setContentPhase('leaving');
          await sleep(CONTENT_EXIT_MS);
          if (cancelled) break;

          const nextIdx = currentIdx + 1;
          syncStepIndex(nextIdx);
          setBarInstant(true);
          setDisplayProgress(0);
          setContentPhase('arriving');
          await sleep(20);
          if (cancelled) break;
          setContentPhase('visible');
          await sleep(20);
          setBarInstant(false);

          if (nextIdx < stepCount - 1) {
            await sleep(CONTENT_ENTER_MS);
          }
        }
      } finally {
        if (!cancelled && advanceGen === advanceGenRef.current) {
          advancingRef.current = false;
          const finalMapped = getVideoGenerationStepProgress(progressRef.current, optionsRef.current);
          if (finalMapped.stepIndex === displayStepIndexRef.current) {
            setDisplayProgress((prev) => Math.max(prev, finalMapped.stepProgress));
          }
        }
      }
    })();

    return () => {
      cancelled = true;
      advanceGenRef.current += 1;
      advancingRef.current = false;
    };
  }, [globalProgress, enabled, includeAiSoundEffects, textOnScreen, noScript]);

  const step = steps[displayStepIndex] || steps[0];

  return {
    title: step.title,
    subtitle: step.subtitle,
    stepIndex: displayStepIndex,
    totalSteps: steps.length,
    stepProgress: displayProgress,
    contentPhase,
    barInstant,
  };
}

export default function VideoGenerationSteps({
  progress,
  compact = false,
  perStepProgress = false,
  showProgressBar = false,
  shimmer = false,
  includeAiSoundEffects = false,
  textOnScreen = false,
  noScript = false,
}) {
  const globalProgress = Math.min(100, Math.max(0, Number(progress) || 0));
  const options = {
    includeAiSoundEffects: includeAiSoundEffects === true,
    textOnScreen: textOnScreen === true,
    noScript: noScript === true,
  };

  const animated = usePerStepProgressAnimation(globalProgress, perStepProgress, options);

  const { title, subtitle, stepProgress, stepIndex, totalSteps, contentPhase, barInstant } = perStepProgress
    ? animated
    : (() => {
        const step = getCurrentVideoGenerationStep(globalProgress, options);
        return {
          title: step.title,
          subtitle: step.subtitle,
          stepProgress: globalProgress,
          stepIndex: null,
          totalSteps: null,
          contentPhase: 'visible',
          barInstant: false,
        };
      })();

  const barWidth = Math.min(100, Math.max(0, stepProgress));
  const barWidthRounded = Math.round(barWidth);
  const shimmerDuration = Math.max(
    360,
    Math.round(1500 * (1 - barWidth / 100)),
  );

  return (
    <div className={`video-gen-steps ${compact ? 'video-gen-steps--compact' : ''}`}>
      {showProgressBar && (
        <div className="video-gen-steps__bar-track">
          <div
            className={[
              shimmer ? 'progress-bar-shimmer-script video-gen-steps__bar-fill' : 'video-gen-steps__bar-fill',
              barInstant ? 'video-gen-steps__bar-fill--instant' : '',
            ].filter(Boolean).join(' ')}
            style={{
              width: `${barWidth}%`,
              ...(shimmer ? { '--shimmer-duration': `${shimmerDuration}ms` } : {}),
            }}
          />
        </div>
      )}
      <div className={`video-gen-steps__text video-gen-steps__text--${contentPhase}`}>
        {perStepProgress && totalSteps != null && (
          <div className="video-gen-steps__counter">
            Step {stepIndex + 1} of {totalSteps}
          </div>
        )}
        <div className="video-gen-steps__title">{title}</div>
        <div className="video-gen-steps__subtitle">{subtitle}</div>
        {showProgressBar && (
          <div className="video-gen-steps__percent">{barWidthRounded}%</div>
        )}
      </div>
    </div>
  );
}
